import React, { useEffect } from "react";
import { Separator } from "../../ui/separator";

const Copyright = () => {

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="flex-grow bg-white py-8 md:py-12">
      <div className="container mx-auto px-2 md:px-4">
        <div className="max-w-4xl mx-auto">
          {/* Page Header */}
          <div className="mb-8 md:mb-12">
            <h1 className="text-3xl font-bold mb-6 text-center text-[#523880]">
              Copyright & DMCA Notice
            </h1>
            <p className="text-sm text-gray-500 text-center mb-8">
              Effective Date: Jun 02, 2025
            </p>
          </div>

          {/* Content */}
          <div className="prose prose-lg max-w-none">
            <section className="mb-8">
              <h2 className="text-xl font-semibold mt-8 mb-4 text-[#EE3788]">
                1. Introduction
              </h2>
              <p className="text-base leading-relaxed text-gray-800">
                Oye Creators (operated by TTL Media Pvt. Ltd.) is an online
                marketplace that connects three types of users:
              </p>
              <ol className="list-decimal pl-5 mb-4 space-y-2">
                <li className="text-base leading-relaxed text-gray-800">
                  Brands & Businesses ("Clients") who post "gigs" to
                  commission content.
                </li>
                <li className="text-base leading-relaxed text-gray-800">
                  Creators who apply for and produce that content, upon
                  confirmation by a Client or the Platform.
                </li>
                <li className="text-base leading-relaxed text-gray-800">
                  Oye Creators (Platform), which facilitates, hosts, and
                  manages all interactions.
                </li>
              </ol>

              <p className="text-base leading-relaxed text-gray-800">
                We respect copyright everywhere we operate. This Notice
                explains:
              </p>
              <ul className="list-disc pl-5 mb-4 space-y-2">
                <li className="text-base leading-relaxed text-gray-800">
                  How to report alleged infringements.
                </li>
                <li className="text-base leading-relaxed text-gray-800">
                  How we respond under U.S. (DMCA) and Indian law.
                </li>
                <li className="text-base leading-relaxed text-gray-800">
                  How you can challenge a removal.
                </li>
                <li className="text-base leading-relaxed text-gray-800">
                  Our policies to protect Oye Creators and its employees.
                </li>
              </ul>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4 text-[#EE3788]">
                2. Key Definitions
              </h2>
              <p className="mb-2">
                <strong>"Infringing Content":</strong> Any text, image, video,
                audio, code or other material that allegedly violates a third
                party's copyright.
              </p>
              <p className="mb-2">
                <strong>"Notice":</strong> A DMCA takedown notice (U.S.) or
                equivalent under Indian law.
              </p>
              <p className="mb-2">
                <strong>"Counter-Notice":</strong> A response by a user
                claiming content was removed in error or qualifies as fair
                use.
              </p>
              <p className="mb-2">
                <strong>"Safe-Harbour":</strong> Limited liability protection
                we enjoy so long as we follow prescribed takedown procedures.
              </p>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-bold mb-4 text-[#EE3788]">
                3. Applicable Legal Framework
              </h2>
              <p className="mb-2">
                <strong>International:</strong> Berne Convention, WIPO
                Copyright Treaty, TRIPS – Member countries must protect
                authors' rights.
              </p>
              <p className="mb-2">
                <strong>United States:</strong> Digital Millennium Copyright
                Act, 17 U.S.C. §512 – Safe-Harbour for OSPs if we remove
                infringing content on notice.
              </p>
              <p className="mb-2">
                <strong>India:</strong> Copyright Act, 1957; Information
                Technology Act, 2000, Sec. 79 – Exemption for intermediaries
                if we remove content upon receiving proper notice.
              </p>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-bold mb-4 text-[#EE3788]">
                4. Our Safe-Harbour Obligations
              </h2>
              <p className="mb-4">
                To maintain immunity from liability for user-uploaded content,
                we must:
              </p>
              <ol className="list-decimal pl-5 mb-4 space-y-2">
                <li>
                  Not have 'actual knowledge' of specific infringing material.
                </li>
                <li>
                  Act expeditiously to remove or disable access once notified.
                </li>
                <li>Designate an agent to receive infringement notices.</li>
                <li>
                  Adopt and reasonably implement a repeat-infringer policy.
                </li>
              </ol>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-bold mb-4 text-[#EE3788]">
                5. Reporting an Infringement
              </h2>
              <h3 className="text-lg font-semibold mb-2">
                5.1. For U.S. Rights-Holders (DMCA Takedown Notice)
              </h3>
              <p className="mb-4">Send a written notice that includes:</p>
              <ol className="list-decimal pl-5 mb-4 space-y-2">
                <li>
                  Identification of the copyrighted work (title, author,
                  registration number if available).
                </li>
                <li>
                  Location (URL or detailed description) of the allegedly
                  infringing material on our platform.
                </li>
                <li>
                  Your contact information: full legal name, mailing address,
                  telephone, and email.
                </li>
                <li>
                  Good-faith statement: "I have a good-faith belief that the
                  use of the material in the manner complained of is not
                  authorized by the copyright owner, its agent, or the law."
                </li>
                <li>
                  Accuracy declaration: "The information in this notice is
                  accurate, and under penalty of perjury, I am authorized to
                  act on behalf of the copyright owner."
                </li>
                <li>Signature: physical or electronic.</li>
              </ol>

              <p className="mb-4">
                <strong>Send To:</strong>
              </p>
              <p className="mb-1">DMCA Designated Agent</p>
              <p className="mb-1">TTL Media Pvt. Ltd. (Oye Creators)</p>
              <p className="mb-1">Quantum Works, CTS No. 177 p, metro station, S. No. 43/2, near Nal stop, Pandurang Colony, Erandwane, Pune, Maharashtra 411004</p>
              <p className="mb-4">Email: dmca@oyecreators.in</p>

              <h3 className="text-lg font-semibold mb-2">
                5.2. For Indian Rights-Holders
              </h3>
              <p className="mb-4">
                Under Section 79 of the IT Act and the Copyright Act, send:
              </p>
              <ul className="list-disc pl-5 mb-4 space-y-2">
                <li>A clear description of your copyrighted work.</li>
                <li>URLs or screenshots of the infringing content.</li>
                <li>
                  Your contact details and proof of ownership or
                  authorization.
                </li>
                <li>
                  A statement under penalty of perjury that you are the
                  rights-holder or agent.
                </li>
              </ul>

              <p className="mb-4">Use the same email: dmca@oyecreators.in.</p>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-bold mb-4 text-[#EE3788]">
                6. Takedown Procedure & Timelines
              </h2>
              <ol className="list-decimal pl-5 mb-4 space-y-2">
                <li>
                  <strong>Acknowledgement (≤ 5 business days):</strong> We'll
                  confirm receipt of your notice.
                </li>
                <li>
                  <strong>Review (≤ 10 business days):</strong> We verify
                  completeness and remove or disable access if valid.
                </li>
                <li>
                  <strong>User Notification:</strong> We inform the uploader,
                  sharing redacted notice details.
                </li>
                <li>
                  <strong>Record-Keeping:</strong> We log notices and removals
                  for at least two years.
                </li>
              </ol>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-bold mb-4 text-[#EE3788]">
                7. Counter-Notification & Content Restoration
              </h2>
              <p className="mb-4">
                If you believe your content was wrongly removed, submit a
                counter-notice containing:
              </p>
              <ol className="list-decimal pl-5 mb-4 space-y-2">
                <li>
                  Identification of the removed content and where it appeared.
                </li>
                <li>
                  Statement under penalty of perjury that removal was a
                  mistake or misidentification.
                </li>
                <li>
                  Consent to U.S. or Indian court jurisdiction (as applicable)
                  and to accept service of process.
                </li>
                <li>Signature (electronic or physical).</li>
              </ol>

              <p className="mb-4">Upon receipt, we will:</p>
              <ul className="list-disc pl-5 mb-4 space-y-2">
                <li>Forward it immediately to the complaining party.</li>
                <li>
                  Wait the statutory period (≥ 10 business days in the U.S.;
                  reasonable time in India).
                </li>
                <li>
                  Restore the content if no lawsuit is filed in that period.
                </li>
              </ul>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-bold mb-4 text-[#EE3788]">
                8. Repeat Infringer Policy
              </h2>
              <p className="mb-4">
                Accounts with multiple valid takedown notices may be suspended
                or terminated. Oye Creators reserves sole discretion in
                enforcing this policy.
              </p>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-bold mb-4 text-[#EE3788]">
                9. Fair Use & Permitted Exceptions
              </h2>
              <p className="mb-4">
                Certain uses of copyrighted material may be lawful without
                authorization, including:
              </p>
              <ul className="list-disc pl-5 mb-4 space-y-2">
                <li>Quotation for critique, review, or commentary.</li>
                <li>Educational use in non-commercial settings.</li>
                <li>Parody, news reporting, or research.</li>
              </ul>

              <p className="mb-4">
                When submitting a counter-notice, explain how your use
                qualifies under fair use or similar exceptions.
              </p>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-bold mb-4 text-[#EE3788]">
                10. Indemnification
              </h2>
              <p className="mb-4">
                To the fullest extent permitted by law, you agree to indemnify
                and hold harmless TTL Media Pvt. Ltd., its officers,
                directors, employees, agents, and affiliates from any claim,
                liability, loss, cost or expense (including attorneys' fees)
                arising out of:
              </p>
              <ul className="list-disc pl-5 mb-4 space-y-2">
                <li>Your content or use of the platform.</li>
                <li>Your breach of this Notice or applicable law.</li>
                <li>Your negligent or willful misconduct.</li>
              </ul>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-bold mb-4 text-[#EE3788]">
                11. Limitation of Liability & Disclaimers
              </h2>
              <ul className="list-disc pl-5 mb-4 space-y-2">
                <li>
                  <strong>No Warranty:</strong> We do not guarantee the
                  accuracy or legality of user-provided content.
                </li>
                <li>
                  <strong>Liability Cap:</strong> In no event shall Oye
                  Creators or its employees be liable for indirect,
                  incidental, special or consequential damages, even if
                  advised of the possibility of such damages.
                </li>
              </ul>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-bold mb-4 text-[#EE3788]">
                12. Amendments & Updates
              </h2>
              <p className="mb-4">
                We may update this Notice at any time. All changes become
                effective upon posting; the Effective Date at the top will be
                revised. Continued use of the platform after changes indicates
                acceptance.
              </p>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-bold mb-4 text-[#EE3788]">
                13. Governing Law & Dispute Resolution
              </h2>
              <ul className="list-disc pl-5 mb-4 space-y-2">
                <li>
                  <strong>India:</strong> All disputes under Indian notices
                  are governed by Indian law, with exclusive jurisdiction in
                  Pune, Maharashtra.
                </li>
                <li>
                  <strong>U.S.:</strong> DMCA matters are governed by U.S.
                  federal law and appropriate U.S. courts.
                </li>
              </ul>
            </section>

            <Separator className="my-8 bg-gray-200" />

            <section className="mb-8">
              <h2 className="text-xl font-bold mb-4 text-[#EE3788]">
                14. Contact Information
              </h2>
              <p className="mb-4">
                For all notices and inquiries, please use:
              </p>
              <p className="mb-1">TTL Media Pvt. Ltd. (Oye Creators)</p>
              <p className="mb-1">Registered Office: Quantum Works, CTS No. 177 p, metro station, S. No. 43/2, near Nal stop, Pandurang Colony, Erandwane, Pune, Maharashtra 411004</p>
              <p className="mb-1">Email: dmca@oyecreators.in</p>
              <p className="mb-1">Phone: +91-8484834105</p>
            </section>
          </div>
        </div>
      </div>
    </main>
  )
}

export default Copyright;
